import React from "react";
import {
  ChannelPreviewUIComponentProps,
  useChatContext,
} from "stream-chat-react";
import { useReservation } from "@/hooks/useReservations";
import ChannelUserAvatar from "@/components/pages/chats/component/chat-custom-components/ChannelUserAvatar";
import { Reservation } from "@/types/reservation";
import { getShortChatDate } from "@/utils/dateUtils";
import {
  Mail,
  MessageCircle,
  MessageSquareText,
  Paperclip,
  Smartphone,
  Star,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { RequestPreview } from "@/components/pages/chats/component/chat-custom-components/RequestPreview";
import { isLastMessageRead } from "@/components/pages/chats/component/chat-custom-components/messageReadStatus";
import { ResolvedChannelValue } from "@/types/chat-external-message";
import { cn } from "@/lib/utils";
import { getRoomShortCode } from "@/components/pages/chats/utils/roomShortCode";
import { useChannelPin } from "@/components/pages/chats/hooks/useChannelPin";

const CHANNEL_ICON: Record<
  Exclude<ResolvedChannelValue, "STREAM_ONLY" | "IN_APP">,
  React.ComponentType<{ className?: string }>
> = {
  WHATSAPP: MessageCircle,
  EMAIL: Mail,
  SMS: Smartphone,
};

const getChannelIcon = (channel?: ResolvedChannelValue) =>
  (channel && CHANNEL_ICON[channel as keyof typeof CHANNEL_ICON]) ||
  MessageSquareText;

export const CustomListItem = (props: ChannelPreviewUIComponentProps) => {
  const { t } = useTranslation();
  const { client } = useChatContext();
  const { channel, active, lastMessage, unread, setActiveChannel } = props;

  const currentUserId = client.userID;

  const chatType = channel?.data?.chat_type;
  const isGroup = chatType === "group";

  const otherMember = Object.values(channel.state.members).find(
    (member) => member.user?.id !== currentUserId,
  )?.user;

  const activeUserName = isGroup
    ? channel.data?.name
    : otherMember?.name ||
      lastMessage?.user?.name ||
      channel.data?.created_by?.name;

  const bookingId =
    channel?.data?.bookingId || channel?.data?.metadata?.bookingId;

  const { data: channelRelatedBooking } =
    useReservation<Reservation>(bookingId);

  const { isPinned } = useChannelPin(channel);

  if (!bookingId) return null;

  const bookingRoom = channelRelatedBooking?.room;
  const roomShortCode = getRoomShortCode(bookingRoom?.category?.name);
  const ChannelIcon = getChannelIcon(lastMessage?.externalChannel);
  const checkingComments = channelRelatedBooking?.guest?.primaryBookings
    ?.map((b) => b?.checkinComments?.trim())
    .filter((c): c is string => Boolean(c && c.length > 0));
  const checkinCommentPreview =
    checkingComments && checkingComments.length > 0
      ? checkingComments[0]
      : channelRelatedBooking?.checkinComments?.trim();

  const attachmentCount = lastMessage?.attachments?.length || 0;
  const isOnlyAttachmentMessage = !lastMessage?.text && attachmentCount > 0;
  const lastMessageRead = isLastMessageRead(
    channel,
    lastMessage,
    currentUserId,
  );

  const renderMessagePreview = () => {
    if (lastMessage?.text) {
      return lastMessage.text;
    }

    if (isOnlyAttachmentMessage) {
      return (
        <span className="flex items-center gap-1 font-medium text-blue-600">
          <Paperclip className="h-3 w-3" />
          {attachmentCount}{" "}
          {attachmentCount === 1
            ? t("chats.attachment")
            : t("chats.attachments")}{" "}
          {t("chats.sent")}
        </span>
      );
    }

    if (lastMessage?.requestId) return;
    return (
      <span className="text-slate-400 italic">{t("chats.emptyChat")}</span>
    );
  };

  const hasUnread = !!unread && unread > 0;

  return (
    <button
      onClick={() => setActiveChannel?.(channel)}
      className={cn(
        "mx-2 mb-2 flex w-[calc(100%-1rem)] flex-col gap-2 rounded-lg border p-2 text-left transition-colors",
        active
          ? "border-royal-glow-3 bg-royal-glow-5 border-2"
          : "border-neutral-200 bg-neutral-50 hover:bg-neutral-100",
      )}
    >
      <div className="flex w-full flex-row items-center gap-2">
        <div className="relative shrink-0">
          <ChannelUserAvatar name={activeUserName} className="size-9" />
          {bookingRoom?.number != null ? (
            <span className="bg-monochrome-core-2 absolute -bottom-1 left-1/2 flex h-4 min-w-5 -translate-x-1/2 items-center justify-center rounded-lg px-1 text-[10px] leading-none font-semibold text-white">
              {bookingRoom.number}
            </span>
          ) : null}
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex flex-row items-center gap-4">
            <div className="flex min-w-0 flex-1 flex-row items-center gap-1.5">
              <span className="text-eathy-comfort-1 truncate text-xs font-medium">
                {activeUserName}
              </span>
              {roomShortCode && (
                <span className="text-light-olive/75 shrink-0 text-xs font-medium">
                  {roomShortCode}
                </span>
              )}
              {isPinned && (
                <Star
                  className="text-fresh-orange size-3 shrink-0 fill-current"
                  aria-label={t("chats.pinned")}
                />
              )}
            </div>
            <div className="flex shrink-0 flex-row items-center gap-1.5">
              {lastMessageRead && (
                <span
                  className="text-monochrome-core-2 text-[10px] font-semibold"
                  aria-label="Read"
                  title="Read"
                >
                  ✓✓
                </span>
              )}
              <ChannelIcon className="text-monochrome-core-3 size-3" />
              {lastMessage?.updated_at && (
                <span className="text-xs font-light whitespace-nowrap text-neutral-900/75">
                  {getShortChatDate(lastMessage.updated_at)}
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-row items-end gap-1">
            <p className="line-clamp-2 min-w-0 flex-1 text-xs break-words text-neutral-900">
              {renderMessagePreview()}
            </p>
            {hasUnread && (
              <span className="bg-royal-glow-3 flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full px-1 text-[10px] font-semibold text-white">
                {unread}
              </span>
            )}
          </div>
        </div>
      </div>

      {!!checkinCommentPreview && (
        <p className="line-clamp-1 text-xs break-words text-neutral-500 italic">
          {t("chats.checkinComments")}: {checkinCommentPreview}
        </p>
      )}

      {lastMessage?.requestId && (
        <RequestPreview requestId={lastMessage.requestId} />
      )}
    </button>
  );
};
